import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import dayjs from 'dayjs';
import { Link } from "react-router-dom"
import { visuallyHidden } from '@mui/utils';
import { Button, Collapse, FormControl, InputLabel, MenuItem, Select, styled, Typography } from '@mui/material';

function isWeeklyFlag(value) {
  return value === true || value === 1 || value === '1';
}

function getEffectiveDayOfWeek(event) {
  const explicitDay = (event.eventDayOfWeek || '').trim();
  if (explicitDay) {
    return explicitDay;
  }

  const startDate = dayjs(event.eventStartDate);
  return startDate.isValid() ? startDate.format('dddd') : '';
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: 'eventId',
    numeric: false,
    disablePadding: true,
    label: 'Event Id',
  },
  {
    id: 'eventType',
    numeric: true,
    disablePadding: false,
    label: 'Event Type',
  },
  {
    id: 'eventTitle',
    numeric: true,
    disablePadding: false,
    label: 'Event Title',
  },
  {
    id: 'eventStartDate',
    numeric: true,
    disablePadding: false,
    label: 'Event Start Date',
  },
  {
    id: 'eventEndDate',
    numeric: true,
    disablePadding: false,
    label: 'Event End Date',
  },
  {
    id: 'isWeeklyEvent',
    numeric: false,
    disablePadding: false,
    label: 'Occurrence',
  },
  {
    id: 'options',
    numeric: true,
    disablePadding: false,
    label: 'Options',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'right'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};



function TableRowDisplay(props) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);
  const getEventType = (eventTypeId) => {
      switch (parseInt(eventTypeId)) {
          case 1:
              return "Music";
          case 2:
              return "Food";
          case 3:
              return "Sport";
          case 4:
              return "Other";
          default:
              return "Unknown Event Type"
      }
  }

  const calculateOccurrence = (row) => {
      if (isWeeklyFlag(row.isWeeklyEvent)) {
          return (<>{calculateDayOfTheWeek(row)}</>)
      }
      else{
          return (<>{calculateDayOfTheWeek(row)}</>)
      }
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({

        height: "300px",

      "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover
      },
      // hide last border
      "&:last-child td, &:last-child th": {
        border: 0,
        height: 20
      }
    }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({

      width: 350,
      maxWidth: 350,
      overflow: "hidden",
      textOverflow: "ellipsis",
      borderStyle: "border-box"
  }));

  const ImageTableCell = styled(TableCell)(({ theme }) => ({
      width: 200,
      maxWidth: 200,
  }));

  const StyledCell = styled(TableCell)(({ theme }) => ({
      width: 100,
      maxWidth: 100,
  }));

  const LargeStyledCell = styled(TableCell)(({ theme }) => ({
      width: 150,
      maxWidth: 150,
  }));
    
    const daysOfWeek = [
      "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
    ]

    const calculateDayOfTheWeek = (row) => {
      var occurrenceText = ""
        if(isWeeklyFlag(row.isWeeklyEvent)){
          occurrenceText = "Every "
      }
      if(row.eventDayOfWeek === "" ||row.eventDayOfWeek === " " || row.eventDayOfWeek === null || row.eventDayOfWeek === undefined){
          var dayOfWeek = dayjs(row.eventStartDate).day()
          var selectedDay = daysOfWeek[dayOfWeek]
          return <>{occurrenceText}{selectedDay}</>
      }else{
          return <>{occurrenceText}{row.eventDayOfWeek}</>
      }
    }

  return (
      <React.Fragment>
          <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
              <TableCell>
                  <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => setOpen(!open)}
                  >
                      {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
              </TableCell>
              <TableCell align="right">{row.eventId}</TableCell>
              <TableCell align="right">{getEventType(row.eventType)}</TableCell>
              <TableCell align="right">{row.eventTitle}</TableCell>
              <TableCell align="right">{dayjs(row.eventStartDate).format("DD-MM-YYYY")}</TableCell>
              <TableCell align="right">{dayjs(row.eventEndDate).format("DD-MM-YYYY")}</TableCell>
              <TableCell align="right">{calculateOccurrence(row)}</TableCell>
              <TableCell align="right"><IconButton aria-label="delete" color="primary" onClick={() => props.handleDelete(row.eventId)}>
                  <DeleteIcon />
              </IconButton> <IconButton aria-label="delete" color="primary" as={Link} to={`/edit-event/${row.eventId}`}>
                      <EditIcon />
                  </IconButton></TableCell>
          </TableRow>
          <TableRow>
              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                          <Typography variant="h6" gutterBottom component="div">
                              Event Details
                          </Typography>
                          <Table size="small" aria-label="purchases">
                              <TableHead>
                                  <TableRow>
                                      <TableCell>Start Date</TableCell>
                                      <TableCell>Start Time</TableCell>
                                      <TableCell>End Date</TableCell>
                                      <TableCell>End Time</TableCell>
                                      <TableCell>Day of the Week</TableCell>
                                      <TableCell>Event Description</TableCell>
                                      <TableCell>Event Image</TableCell>
                                  </TableRow>
                              </TableHead>
                              <TableBody>
                                  <StyledTableRow key={row.eventId}>
                                      <StyledCell component="th" scope="row">
                                          {dayjs(row.eventStartDate).format("DD-MM-YYYY")}
                                      </StyledCell>
                                      <StyledCell>{row.eventStartTime.slice(0, -3)}</StyledCell>
                                      <StyledCell>{dayjs(row.eventEndDate).format("DD-MM-YYYY")}</StyledCell>
                                      <StyledCell>{row.eventEndTime.slice(0, -3)}</StyledCell>
                                      <LargeStyledCell>{calculateDayOfTheWeek(row)}</LargeStyledCell>
                                      <StyledTableCell>{row.eventDescription}</StyledTableCell>
                                      <ImageTableCell >
                                          <img src={row.eventImage} alt="" style={{ maxHeight: 200, maxWidth:200 }} />
                                      </ImageTableCell>
                                  </StyledTableRow>
                              </TableBody>
                          </Table>
                      </Box>
                  </Collapse>
              </TableCell>
          </TableRow>
      </React.Fragment>
  );
}


export default function SortableEventTable(props) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('eventId');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [eventType, setEventType] = React.useState('');
  const [occurrence, setOccurrence] = React.useState('');
  const [dayOfWeek, setDayOfWeek] = React.useState('')
  const [filtersVisible, setFiltersVisible] = React.useState(false);

  const daysOfWeek = [
    { label: 'Sunday', value: 'Sunday' },
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
    { label: 'Saturday', value: 'Saturday' }
  ]

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };  const handleEventTypeChange = (event) => {
    setEventType(event.target.value);
  };

  const handleOccurrenceChange = (event) => {
    setOccurrence(event.target.value);
  };

  const handleDayOfWeekChange = (event) => {
    setDayOfWeek(event.target.value);
  };



const handleFiltersToggle = () => {
  setFiltersVisible(!filtersVisible);
};

  const filteredEvents = props.events.filter((event) => {
    const isWeeklyEvent = isWeeklyFlag(event.isWeeklyEvent);
    const effectiveDayOfWeek = getEffectiveDayOfWeek(event);

    return (eventType === '' || event.eventType === eventType) &&
      (occurrence === '' || (occurrence === 'weekly' && isWeeklyEvent) || (occurrence === 'non-weekly' && !isWeeklyEvent)) &&
      (dayOfWeek === '' || effectiveDayOfWeek === dayOfWeek);
  });

  const visibleRows = React.useMemo(
    () =>
      [...filteredEvents]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredEvents]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
      <Button variant="contained" onClick={handleFiltersToggle}>
            {filtersVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2, '@media (min-width:600px)': { flexDirection: 'row', alignItems: 'center' } }}>
              <Typography variant="h6" component="div" sx={{ mr: 2 }}>
                Filters:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', '@media (min-width:600px)': { flexDirection: 'row', alignItems: 'center' } }}>
                <FormControl sx={{ minWidth: 120, mr: 2, mt: 1, '@media (min-width:600px)': { mt: 0 } }}>
                  <InputLabel id="event-type-label">Event Type</InputLabel>
                  <Select
                    labelId="event-type-label"
                    value={eventType}
                    onChange={handleEventTypeChange}
                    label="Event Type"
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value={1}>Music</MenuItem>
                    <MenuItem value={2}>Food</MenuItem>
                    <MenuItem value={3}>Sport</MenuItem>
                    <MenuItem value={4}>Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 150, mr: 2, mt: 1, '@media (min-width:600px)': { mt: 0 } }}>
                  <InputLabel id="occurrence-label">Occurrence</InputLabel>
                  <Select
                    labelId="occurrence-label"
                    value={occurrence}
                    onChange={handleOccurrenceChange}
                    label="Occurrence"
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='weekly'>Weekly</MenuItem>
                    <MenuItem value='non-weekly'>Non-Weekly</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 160, mr: 2, mt: 1, '@media (min-width:600px)': { mt: 0 } }}>
                  <InputLabel id="day-of-week-label">Day of the Week</InputLabel>
                  <Select
                    labelId="day-of-week-label"
                    value={dayOfWeek}
                    onChange={handleDayOfWeekChange}
                    label="Day of the Week"
                  >
                    <MenuItem value=''>All</MenuItem>
                    {daysOfWeek.map(day => (
                      <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          
          </Collapse>
        </Box>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.events.length}
            />
            <TableBody>
            {visibleRows.map((row) => (
                            <TableRowDisplay key={row.eventId} row={row} handleDelete={() => props.handleDelete(row.eventId)} />
                        ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 15, 25]}
          component="div"
          count={filteredEvents.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
      />
    </Box>
  );
}